import { WORKING_SET_TYPES, type SetType } from './enums.js';
import type { LoggedSet, PrescribedSet } from './types.js';

/**
 * Set mechanics.
 *
 * One place defines what "a set" means for counting, volume and records, so the
 * rules cannot drift apart between the PC and the phone.
 *
 * The three rules that matter:
 *  1. A **primary set** has `dropIndex === 0` and no parent.
 *  2. A **drop stage** hangs off a primary set. It is recorded and counts
 *     toward volume, but never toward a prescription and never toward a PR.
 *  3. A **warm-up** is recorded and visible in history, but counts toward
 *     nothing — not prescription, not volume, not records.
 */

const WORKING_SET_TYPE_SET: ReadonlySet<SetType> = new Set(WORKING_SET_TYPES);

/** A primary set: the thing a prescription counts. */
export function isPrimarySet(set: LoggedSet): boolean {
  return set.dropIndex === 0 && set.parentSetId === null;
}

/** A drop stage attached to a primary set. */
export function isDropStage(set: LoggedSet): boolean {
  return set.dropIndex > 0 && set.parentSetId !== null;
}

export function isWarmup(set: LoggedSet): boolean {
  return set.setType === 'warmup';
}

/** A working set type — `normal`, `amrap` or `failure`. Excludes warm-ups and drops. */
export function isWorkingSetType(setType: SetType): boolean {
  return WORKING_SET_TYPE_SET.has(setType);
}

/**
 * A completed prescribed working set.
 *
 * This is the exact rule approved in Phase 0 and enforced in the Phase 2 schema:
 * primary set, working type, and actually completed.
 */
export function countsTowardPrescription(set: LoggedSet): boolean {
  return isPrimarySet(set) && isWorkingSetType(set.setType) && set.isCompleted;
}

/** How many prescribed working sets are complete. */
export function countCompletedWorkingSets(sets: readonly LoggedSet[]): number {
  return sets.filter((s) => countsTowardPrescription(s)).length;
}

/**
 * Whether a set contributes to strength volume.
 *
 * Drop stages **are** included — they were genuinely performed and the user
 * asked for them in volume. Warm-ups and incomplete sets are not.
 */
export function countsTowardVolume(set: LoggedSet): boolean {
  return set.isCompleted && !isWarmup(set) && set.loadType !== 'none';
}

/**
 * Whether a set may set a personal record.
 *
 * Excludes warm-ups, incomplete sets and drop stages. A 40 kg drop after a hard
 * top set is not a record, and counting it would pollute the rep-max table.
 */
export function isPrEligible(set: LoggedSet): boolean {
  return set.isCompleted && isPrimarySet(set) && isWorkingSetType(set.setType);
}

/** Primary sets only, in order. */
export function primarySets(sets: readonly LoggedSet[]): LoggedSet[] {
  return sets.filter((s) => isPrimarySet(s)).sort((a, b) => a.setIndex - b.setIndex);
}

/** Working sets only (primary, non-warm-up), in order. */
export function workingSets(sets: readonly LoggedSet[]): LoggedSet[] {
  return primarySets(sets).filter((s) => isWorkingSetType(s.setType));
}

/** Warm-up sets only, in order. */
export function warmupSets(sets: readonly LoggedSet[]): LoggedSet[] {
  return primarySets(sets).filter((s) => s.setType === 'warmup');
}

/** Drop stages belonging to a given primary set, in drop order. */
export function dropStagesOf(sets: readonly LoggedSet[], primarySetId: string): LoggedSet[] {
  return sets
    .filter((s) => s.parentSetId === primarySetId)
    .sort((a, b) => a.dropIndex - b.dropIndex);
}

/**
 * A primary set together with its drop stages.
 * The shape the workout screen and the volume engine both want.
 */
export interface SetGroup {
  primary: LoggedSet;
  drops: LoggedSet[];
}

export function groupSets(sets: readonly LoggedSet[]): SetGroup[] {
  return primarySets(sets).map((primary) => ({
    primary,
    drops: dropStagesOf(sets, primary.id),
  }));
}

export interface PrescriptionProgress {
  prescribed: number;
  completed: number;
  remaining: number;
  isComplete: boolean;
  /** Warm-ups logged; shown in history but excluded from the count. */
  warmupsLogged: number;
  dropStagesLogged: number;
  incompleteLogged: number;
}

/**
 * Progress against a prescription.
 *
 * Only primary working sets are counted, so a prescribed "3 sets" stays 3 sets
 * however many warm-ups or drop stages were also logged.
 */
export function prescriptionProgress(
  prescribed: readonly PrescribedSet[],
  logged: readonly LoggedSet[],
): PrescriptionProgress {
  const prescribedCount = prescribed.filter(
    (p) => p.dropIndex === 0 && isWorkingSetType(p.setType),
  ).length;
  const completed = countCompletedWorkingSets(logged);

  return {
    prescribed: prescribedCount,
    completed,
    remaining: Math.max(0, prescribedCount - completed),
    isComplete: prescribedCount > 0 && completed >= prescribedCount,
    warmupsLogged: logged.filter((s) => isWarmup(s) && isPrimarySet(s)).length,
    dropStagesLogged: logged.filter((s) => isDropStage(s)).length,
    incompleteLogged: logged.filter((s) => !s.isCompleted).length,
  };
}

/**
 * Structural validation of a set collection.
 *
 * The database enforces these too; this exists so the phone can reject a
 * malformed set before it ever enters the offline queue, rather than having it
 * fail on the PC hours later.
 */
export interface SetValidationIssue {
  setId: string;
  code:
    | 'drop_without_parent'
    | 'primary_with_parent'
    | 'parent_not_found'
    | 'parent_is_drop_stage'
    | 'duplicate_position'
    | 'negative_reps'
    | 'negative_duration'
    | 'rpe_out_of_range';
  detail: string;
}

export function validateSets(sets: readonly LoggedSet[]): SetValidationIssue[] {
  const issues: SetValidationIssue[] = [];
  const byId = new Map(sets.map((s) => [s.id, s]));
  const positions = new Set<string>();

  for (const set of sets) {
    if (set.dropIndex > 0 && set.parentSetId === null) {
      issues.push({
        setId: set.id,
        code: 'drop_without_parent',
        detail: `Drop stage ${String(set.dropIndex)} has no parent set`,
      });
    }
    if (set.dropIndex === 0 && set.parentSetId !== null) {
      issues.push({
        setId: set.id,
        code: 'primary_with_parent',
        detail: 'A primary set must not reference a parent',
      });
    }
    if (set.parentSetId !== null) {
      const parent = byId.get(set.parentSetId);
      if (parent === undefined) {
        issues.push({
          setId: set.id,
          code: 'parent_not_found',
          detail: `Parent set '${set.parentSetId}' is not present`,
        });
      } else if (parent.dropIndex !== 0) {
        issues.push({
          setId: set.id,
          code: 'parent_is_drop_stage',
          detail: 'Drop stages cannot be chained off another drop stage',
        });
      }
    }

    const position = `${set.sessionExerciseId}#${String(set.setIndex)}#${String(set.dropIndex)}`;
    if (positions.has(position)) {
      issues.push({
        setId: set.id,
        code: 'duplicate_position',
        detail: `Another set already occupies position ${position}`,
      });
    }
    positions.add(position);

    if (set.reps !== null && set.reps < 0) {
      issues.push({ setId: set.id, code: 'negative_reps', detail: `reps = ${String(set.reps)}` });
    }
    if (set.durationS !== null && set.durationS < 0) {
      issues.push({
        setId: set.id,
        code: 'negative_duration',
        detail: `duration = ${String(set.durationS)}`,
      });
    }
    if (set.rpe !== null && (set.rpe < 1 || set.rpe > 10)) {
      issues.push({ setId: set.id, code: 'rpe_out_of_range', detail: `rpe = ${String(set.rpe)}` });
    }
  }

  return issues;
}
